import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCustomer } from "../lib/customerAuth";
import { PeptigenLogo } from "../components/Logo";
import { ShieldCheck, KeyRound, UserPlus, LogIn } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "../lib/usePageMeta";

export default function AccountLogin() {
  const { login, register, customer } = useCustomer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  usePageMeta(
    mode === "register" ? "Create Account" : "Sign In",
    "Access your Peptigen Labs account, view orders, and track UK dispatch."
  );

  React.useEffect(() => {
    if (customer) navigate("/account");
  }, [customer, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "register") {
        await register(form.email, form.password, form.name);
        toast.success("Welcome to Peptigen Labs");
      } else {
        await login(form.email, form.password);
        toast.success("Signed in");
      }
      navigate("/account");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (mode === "register" ? "Registration failed" : "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden py-12"
      style={{ backgroundColor: "#0B1220" }}
      data-testid="account-login-page"
    >
      <div className="absolute inset-0 pg-grid-bg opacity-40" />
      <div className="absolute inset-0 pg-radial-cyan" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/"><PeptigenLogo size={42} /></Link>
        </div>

        <div className="pg-card p-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} className="text-[#5EEAD4]" />
            <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]">
              Customer Account
            </span>
          </div>
          <h1 className="text-[#F7FAFC] text-2xl pg-heading mb-1" style={{ fontFamily: "'Rajdhani'" }}>
            {mode === "register" ? "Create your account" : "Sign in to your account"}
          </h1>
          <p className="text-[#9CA8B8] text-sm mb-6">
            {mode === "register"
              ? "Track orders, save your shipping address, and get faster checkout."
              : "View your orders, dispatch updates, and tracking numbers."}
          </p>

          <div className="grid grid-cols-2 gap-1 p-1 mb-6 border border-[#00C2D6]/20 rounded-sm">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`py-2 text-xs pg-mono uppercase tracking-[0.18em] rounded-sm transition-colors ${
                mode === "login" ? "bg-[#00C2D6]/15 text-[#00C2D6]" : "text-[#9CA8B8]"
              }`}
              data-testid="login-tab"
            >
              <LogIn size={12} className="inline mr-1" /> Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`py-2 text-xs pg-mono uppercase tracking-[0.18em] rounded-sm transition-colors ${
                mode === "register" ? "bg-[#A6FF00]/15 text-[#A6FF00]" : "text-[#9CA8B8]"
              }`}
              data-testid="register-tab"
            >
              <UserPlus size={12} className="inline mr-1" /> Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <div className="pg-micro mb-2">Full Name</div>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pg-input"
                  placeholder="Dr Jane Smith"
                  data-testid="register-name"
                />
              </div>
            )}
            <div>
              <div className="pg-micro mb-2">Email</div>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="pg-input"
                placeholder="you@lab.ac.uk"
                data-testid="account-email"
              />
            </div>
            <div>
              <div className="pg-micro mb-2">Password {mode === "register" && <span className="text-[#9CA8B8] normal-case tracking-normal">(min 8 chars)</span>}</div>
              <input
                type="password"
                required
                minLength={mode === "register" ? 8 : 1}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="pg-input"
                data-testid="account-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 border border-red-400/40 bg-red-400/10 p-3 rounded-sm" data-testid="account-error">
                {error}
              </div>
            )}
            {mode === "login" && (
              <div className="text-right -mt-1">
                <Link
                  to={`/account/forgot${form.email ? `?email=${encodeURIComponent(form.email)}` : ""}`}
                  className="text-xs text-[#00C2D6] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
            <button type="submit" disabled={submitting} className="pg-btn-primary w-full" data-testid="account-submit">
              <KeyRound size={16} />
              {submitting ? "Working..." : mode === "register" ? "Create Account" : "Sign In"}
            </button>
          </form>

          <p className="mt-5 text-xs text-[#9CA8B8] text-center leading-relaxed">
            By creating an account you agree to our <Link to="/legal/terms" className="text-[#00C2D6] hover:underline">Terms</Link> &
            <Link to="/legal/privacy" className="text-[#00C2D6] hover:underline"> Privacy Policy</Link>.
            Account use is restricted to qualified UK research customers.
          </p>
        </div>

        <p className="mt-4 text-center text-xs text-[#9CA8B8]">
          Need help? <Link to="/contact" className="text-[#00C2D6] hover:underline">Contact our UK team</Link>
        </p>
      </div>
    </div>
  );
}
